import {
    Alert,
    AlertDescription,
    AlertIcon,
    Box,
    Button,
    Flex,
    FormControl,
    FormHelperText,
    FormLabel,
    IconButton,
    Input,
    Select,
    SimpleGrid,
    Spinner,
    Stack,
    Switch,
    Text,
    useToast,
} from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { MdAdd, MdDelete, MdRocketLaunch } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import Card from 'components/card/Card';
import FormSection from 'components/form-section';
import PageCard from 'components/page-card';
import EmptyState from 'components/states/EmptyState';
import { spacing, typography, useSemanticColor } from 'theme/tokens';
import { translate } from '../../i18n';
import { SportType } from '../../models/court/dto/court.dto';
import { OrganizationDto } from '../../models/organization/dto/organization.dto';
import {
    ProvisionAdminAccessMode,
    ProvisionOrganizationOutputDto,
    ProvisionStepStatus,
} from '../../models/organization-provisioning/dto/provision-organization.dto';
import { MY_ORGANIZATIONS_QUERY_KEY } from '../organization/hooks/useGetMyOrganizations';
import { useElevatedAdminAccess } from '../organization/hooks/useIsElevatedAdmin';
import ElevationUnknownNotice from '../_shared/ElevationUnknownNotice';
import { useSelectedOrganization } from '../organization/hooks/useSelectedOrganization';
import { dashboardSignUpUrl } from '../organization/utils/dashboard-sign-up-url';
import { useProvisionOrganization } from './hooks/useProvisionOrganization';
import {
    COMMITMENT_OPTIONS,
    createInitialForm,
    newCoachRow,
    newCourtRow,
    newMembershipRow,
    ProvisioningForm,
    SLOT_DURATION_OPTIONS,
    syncPricingWithCourts,
    toProvisionPayload,
} from './utils/provisioning-form';

const SPORTS = Object.values(SportType).sort();

const formatCents = (cents: number) => (cents / 100).toFixed(2);

/**
 * Superadmin tool: build a prospect's club (org, Stripe account, courts with
 * priced slots, coaches, membership offers) in one call before the onboarding
 * video call, then jump into it to demo and adjust.
 */
export default function ProvisionOrganizationPage() {
    const { state: elevationState, isElevated } = useElevatedAdminAccess();
    const checkingElevated = elevationState === 'checking';
    const [form, setForm] = useState<ProvisioningForm>(createInitialForm);
    const [result, setResult] = useState<ProvisionOrganizationOutputDto | null>(null);
    const { mutateAsync, isLoading } = useProvisionOrganization();
    const toast = useToast();
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const setSelectedOrganization = useSelectedOrganization.use.setSelectedOrganization();
    const mutedColor = useSemanticColor('textMuted');
    const textColor = useSemanticColor('textPrimary');

    const update = (patch: Partial<ProvisioningForm>) => setForm((f) => ({ ...f, ...patch }));
    const updateCourts = (courts: ProvisioningForm['courts']) =>
        setForm((f) => ({ ...f, courts, pricing: syncPricingWithCourts(f.pricing, courts) }));
    const updateRow = <K extends 'courts' | 'coaches' | 'memberships'>(
        list: K,
        key: string,
        patch: Partial<ProvisioningForm[K][number]>,
    ) => {
        const rows = (form[list] as ProvisioningForm[K][number][]).map((row) =>
            row.key === key ? { ...row, ...patch } : row,
        ) as ProvisioningForm[K];
        if (list === 'courts') updateCourts(rows as ProvisioningForm['courts']);
        else update({ [list]: rows } as Partial<ProvisioningForm>);
    };

    if (checkingElevated) {
        return (
            <Flex justify="center" py={10}>
                <Spinner color="brand.500" />
            </Flex>
        );
    }
    // A failed elevation read is not a denial: say so rather than telling a
    // superadmin this tool is not for them (same split as RoadmapMappingPage).
    if (elevationState === 'unknown') {
        return (
            <Card>
                <ElevationUnknownNotice />
            </Card>
        );
    }
    if (isElevated !== true) {
        return (
            <Card>
                <EmptyState
                    icon={MdRocketLaunch}
                    title={translate('org_provisioning.restricted_title')}
                    description={translate('org_provisioning.restricted_desc')}
                />
            </Card>
        );
    }

    const submit = async () => {
        let payload;
        try {
            payload = toProvisionPayload(form);
        } catch (error) {
            toast({ title: (error as Error).message, status: 'error', duration: 5000, isClosable: true });
            return;
        }
        try {
            setResult(await mutateAsync(payload));
        } catch (error) {
            toast({
                title: translate('org_provisioning.error'),
                description: (error as Error).message,
                status: 'error',
                duration: 8000,
                isClosable: true,
            });
        }
    };

    const openOrganization = async () => {
        await queryClient.invalidateQueries({ queryKey: MY_ORGANIZATIONS_QUERY_KEY });
        const organizations = queryClient.getQueryData<OrganizationDto[]>(MY_ORGANIZATIONS_QUERY_KEY);
        const organization = organizations?.find((o) => o.id === result?.organization.id);
        if (!organization) {
            toast({ title: translate('org_provisioning.open_fallback'), status: 'info', duration: 5000 });
            return;
        }
        setSelectedOrganization(organization);
        navigate('/admin/home');
    };

    if (result) {
        return (
            <PageCard
                title={translate('org_provisioning.success_title', { name: result.organization.name })}
                rightHeader={
                    <Flex gap={spacing.inlineGap}>
                        <Button variant="outline" onClick={() => { setResult(null); setForm(createInitialForm()); }}>
                            {translate('org_provisioning.provision_another')}
                        </Button>
                        <Button colorScheme="brand" onClick={openOrganization}>
                            {translate('org_provisioning.open_organization')}
                        </Button>
                    </Flex>
                }
            >
                <Stack px={spacing.cardHeader} pb={spacing.card} spacing={spacing.fieldGap}>
                    {result.warnings.map((warning) => (
                        <Alert key={warning} status="warning" borderRadius="md">
                            <AlertIcon />
                            <AlertDescription>{warning}</AlertDescription>
                        </Alert>
                    ))}
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={spacing.fieldGap}>
                        <Box>
                            <Text {...typography.formLabel} color={mutedColor}>{translate('org_provisioning.result_stripe')}</Text>
                            <Text fontFamily="mono" color={textColor}>{result.stripeAccountId}</Text>
                        </Box>
                        <Box>
                            <Text {...typography.formLabel} color={mutedColor}>{translate('org_provisioning.result_invitation')}</Text>
                            <Text color={textColor}>
                                {result.adminAccess.status === ProvisionStepStatus.DONE
                                    ? result.adminAccess.mode === ProvisionAdminAccessMode.ADD
                                        ? translate('org_provisioning.admin_added', { email: result.adminAccess.email ?? '' })
                                        : translate('org_provisioning.invitation_sent', { email: result.adminAccess.email ?? '' })
                                    : result.adminAccess.status === ProvisionStepStatus.FAILED
                                        ? translate('org_provisioning.invitation_failed')
                                        : translate('org_provisioning.invitation_skipped')}
                            </Text>
                        </Box>
                    </SimpleGrid>
                    {result.adminAccess.mode === ProvisionAdminAccessMode.ADD
                        && result.adminAccess.status === ProvisionStepStatus.DONE && (
                        // The one link to hand over: they sign up with the added
                        // email and the club is already theirs (matched by email).
                        <Alert status="info" borderRadius="md">
                            <AlertIcon />
                            <AlertDescription>
                                {translate('org_provisioning.admin_sign_up_hint', {
                                    email: result.adminAccess.email ?? '',
                                    url: dashboardSignUpUrl(),
                                })}
                            </AlertDescription>
                        </Alert>
                    )}
                    <Box>
                        <Text {...typography.formLabel} color={mutedColor}>{translate('org_provisioning.courts')}</Text>
                        {result.courts.map((court) => (
                            <Text key={court.id} color={textColor}>
                                {translate('org_provisioning.result_court', {
                                    name: court.name,
                                    sport: court.sport,
                                    count: court.availabilityCount,
                                })}
                            </Text>
                        ))}
                    </Box>
                    <Box>
                        <Text {...typography.formLabel} color={mutedColor}>{translate('org_provisioning.coaches')}</Text>
                        {result.coaches.length === 0 && <Text color={mutedColor}>—</Text>}
                        {result.coaches.map((coach) => (
                            <Text key={coach.id} color={textColor}>{coach.firstName} {coach.lastName}</Text>
                        ))}
                    </Box>
                    <Box>
                        <Text {...typography.formLabel} color={mutedColor}>{translate('org_provisioning.memberships')}</Text>
                        {result.membershipOffers.length === 0 && <Text color={mutedColor}>—</Text>}
                        {result.membershipOffers.map((offer) => (
                            <Text key={offer.membershipTypeId} color={textColor}>
                                {translate('org_provisioning.result_membership', {
                                    name: offer.name,
                                    price: formatCents(offer.priceInCents),
                                    months: offer.commitmentMonths,
                                })}
                            </Text>
                        ))}
                    </Box>
                    <Text {...typography.caption} color={mutedColor}>{translate('org_provisioning.next_steps')}</Text>
                </Stack>
            </PageCard>
        );
    }

    return (
        <PageCard
            title={translate('org_provisioning.title')}
            rightHeader={
                <Button
                    colorScheme="brand"
                    leftIcon={<MdRocketLaunch />}
                    isLoading={isLoading}
                    loadingText={translate('org_provisioning.submitting')}
                    isDisabled={form.name.trim().length < 2}
                    onClick={submit}
                >
                    {translate('org_provisioning.submit')}
                </Button>
            }
        >
            <Stack px={spacing.cardHeader} pb={spacing.card} spacing={spacing.sectionGap}>
                <Text color={mutedColor} {...typography.body}>{translate('org_provisioning.subtitle')}</Text>

                <FormSection label={translate('org_provisioning.club')} columns={{ base: 1, md: 2 }}>
                    <FormControl isRequired>
                        <FormLabel>{translate('org_provisioning.name')}</FormLabel>
                        <Input value={form.name} onChange={(e) => update({ name: e.target.value })} maxLength={120} />
                    </FormControl>
                    <FormControl>
                        <FormLabel>{translate('org_provisioning.admin_email')}</FormLabel>
                        <Input type="email" value={form.adminEmail} onChange={(e) => update({ adminEmail: e.target.value })} />
                        <Select
                            mt={2}
                            aria-label={translate('org_provisioning.admin_access_mode')}
                            value={form.adminAccessMode}
                            isDisabled={!form.adminEmail.trim()}
                            onChange={(e) => update({ adminAccessMode: e.target.value as ProvisionAdminAccessMode })}
                        >
                            <option value={ProvisionAdminAccessMode.ADD}>{translate('org_provisioning.admin_access_add')}</option>
                            <option value={ProvisionAdminAccessMode.INVITE}>{translate('org_provisioning.admin_access_invite')}</option>
                        </Select>
                        <FormHelperText>{translate('org_provisioning.admin_email_help')}</FormHelperText>
                    </FormControl>
                    <FormControl>
                        <FormLabel>{translate('org_provisioning.address')}</FormLabel>
                        <Input value={form.address} onChange={(e) => update({ address: e.target.value })} />
                    </FormControl>
                    <SimpleGrid columns={2} spacing={spacing.inlineGap}>
                        <FormControl>
                            <FormLabel>{translate('org_provisioning.postal_code')}</FormLabel>
                            <Input value={form.postalCode} onChange={(e) => update({ postalCode: e.target.value })} />
                        </FormControl>
                        <FormControl>
                            <FormLabel>{translate('org_provisioning.city')}</FormLabel>
                            <Input value={form.city} onChange={(e) => update({ city: e.target.value })} />
                        </FormControl>
                    </SimpleGrid>
                </FormSection>

                <FormSection label={translate('org_provisioning.courts')}>
                    {form.courts.map((court) => (
                        <Flex key={court.key} gap={spacing.inlineGap} align="center">
                            <Input
                                aria-label={translate('org_provisioning.court_name')}
                                value={court.name}
                                onChange={(e) => updateRow('courts', court.key, { name: e.target.value })}
                            />
                            <Select
                                aria-label={translate('org_provisioning.sport')}
                                maxW="200px"
                                value={court.sport}
                                onChange={(e) => updateRow('courts', court.key, { sport: e.target.value as SportType })}
                            >
                                {SPORTS.map((sport) => <option key={sport} value={sport}>{sport}</option>)}
                            </Select>
                            <FormControl display="flex" alignItems="center" w="auto">
                                <FormLabel mb={0} whiteSpace="nowrap">{translate('org_provisioning.outdoor')}</FormLabel>
                                <Switch isChecked={court.isOutdoor} onChange={(e) => updateRow('courts', court.key, { isOutdoor: e.target.checked })} />
                            </FormControl>
                            <IconButton
                                aria-label={translate('org_provisioning.remove')}
                                icon={<MdDelete />}
                                variant="ghost"
                                onClick={() => updateCourts(form.courts.filter((c) => c.key !== court.key))}
                            />
                        </Flex>
                    ))}
                    <Box>
                        <Button size="sm" variant="ghost" leftIcon={<MdAdd />} isDisabled={form.courts.length >= 12}
                            onClick={() => updateCourts([...form.courts, newCourtRow(form.courts.length + 1, form.courts[0]?.sport)])}>
                            {translate('org_provisioning.add_court')}
                        </Button>
                    </Box>
                </FormSection>

                <FormSection label={translate('org_provisioning.hours_and_prices')} columns={{ base: 1, md: 2 }}>
                    <SimpleGrid columns={3} spacing={spacing.inlineGap}>
                        <FormControl>
                            <FormLabel>{translate('org_provisioning.opening')}</FormLabel>
                            <Input type="time" value={form.openingTime} onChange={(e) => update({ openingTime: e.target.value })} />
                        </FormControl>
                        <FormControl>
                            <FormLabel>{translate('org_provisioning.closing')}</FormLabel>
                            <Input value={form.closingTime} placeholder="23:00" onChange={(e) => update({ closingTime: e.target.value })} />
                        </FormControl>
                        <FormControl>
                            <FormLabel>{translate('org_provisioning.peak_start')}</FormLabel>
                            <Input type="time" value={form.weekdayPeakStartTime} onChange={(e) => update({ weekdayPeakStartTime: e.target.value })} />
                        </FormControl>
                    </SimpleGrid>
                    <FormControl display="flex" alignItems="center">
                        <FormLabel mb={0}>{translate('org_provisioning.weekend_peak')}</FormLabel>
                        <Switch isChecked={form.weekendAllDayPeak} onChange={(e) => update({ weekendAllDayPeak: e.target.checked })} />
                    </FormControl>
                    {form.pricing.map((row) => (
                        <SimpleGrid key={row.sport} columns={3} spacing={spacing.inlineGap} gridColumn="1 / -1">
                            <FormControl>
                                <FormLabel>{translate('org_provisioning.slot_length', { sport: row.sport })}</FormLabel>
                                <Select
                                    value={row.slotDurationMinutes}
                                    onChange={(e) => update({
                                        pricing: form.pricing.map((p) => p.sport === row.sport ? { ...p, slotDurationMinutes: Number(e.target.value) } : p),
                                    })}
                                >
                                    {SLOT_DURATION_OPTIONS.map((m) => <option key={m} value={m}>{m} min</option>)}
                                </Select>
                            </FormControl>
                            <FormControl>
                                <FormLabel>{translate('org_provisioning.off_peak_price')}</FormLabel>
                                <Input inputMode="decimal" value={row.offPeakPrice}
                                    onChange={(e) => update({ pricing: form.pricing.map((p) => p.sport === row.sport ? { ...p, offPeakPrice: e.target.value } : p) })} />
                            </FormControl>
                            <FormControl>
                                <FormLabel>{translate('org_provisioning.peak_price')}</FormLabel>
                                <Input inputMode="decimal" value={row.peakPrice}
                                    onChange={(e) => update({ pricing: form.pricing.map((p) => p.sport === row.sport ? { ...p, peakPrice: e.target.value } : p) })} />
                            </FormControl>
                        </SimpleGrid>
                    ))}
                    <Text {...typography.caption} color={mutedColor} gridColumn="1 / -1">{translate('org_provisioning.prices_help')}</Text>
                </FormSection>

                <FormSection label={translate('org_provisioning.coaches')}>
                    {form.coaches.map((coach) => (
                        <Flex key={coach.key} gap={spacing.inlineGap} align="center">
                            <Input placeholder={translate('org_provisioning.first_name')} value={coach.firstName}
                                onChange={(e) => updateRow('coaches', coach.key, { firstName: e.target.value })} />
                            <Input placeholder={translate('org_provisioning.last_name')} value={coach.lastName}
                                onChange={(e) => updateRow('coaches', coach.key, { lastName: e.target.value })} />
                            <Input placeholder="+33612345678" value={coach.phone}
                                onChange={(e) => updateRow('coaches', coach.key, { phone: e.target.value })} />
                            <Input maxW="120px" inputMode="decimal" aria-label={translate('org_provisioning.hourly_rate')} value={coach.hourlyRate}
                                onChange={(e) => updateRow('coaches', coach.key, { hourlyRate: e.target.value })} />
                            <IconButton aria-label={translate('org_provisioning.remove')} icon={<MdDelete />} variant="ghost"
                                onClick={() => update({ coaches: form.coaches.filter((c) => c.key !== coach.key) })} />
                        </Flex>
                    ))}
                    <Box>
                        <Button size="sm" variant="ghost" leftIcon={<MdAdd />} onClick={() => update({ coaches: [...form.coaches, newCoachRow()] })}>
                            {translate('org_provisioning.add_coach')}
                        </Button>
                    </Box>
                </FormSection>

                <FormSection label={translate('org_provisioning.memberships')}>
                    {form.memberships.map((offer) => (
                        <Flex key={offer.key} gap={spacing.inlineGap} align="center">
                            <Input placeholder={translate('org_provisioning.offer_name')} value={offer.name}
                                onChange={(e) => updateRow('memberships', offer.key, { name: e.target.value })} />
                            <Input maxW="140px" inputMode="decimal" aria-label={translate('org_provisioning.monthly_price')}
                                placeholder={translate('org_provisioning.monthly_price')} value={offer.monthlyPrice}
                                onChange={(e) => updateRow('memberships', offer.key, { monthlyPrice: e.target.value })} />
                            <Select maxW="170px" aria-label={translate('org_provisioning.commitment')} value={offer.commitmentMonths}
                                onChange={(e) => updateRow('memberships', offer.key, { commitmentMonths: Number(e.target.value) })}>
                                {COMMITMENT_OPTIONS.map((m) => (
                                    <option key={m} value={m}>{translate('org_provisioning.commitment_months', { months: m })}</option>
                                ))}
                            </Select>
                            <Input maxW="120px" inputMode="decimal" aria-label={translate('org_provisioning.court_discount')}
                                placeholder="%" value={offer.courtBookingDiscountPercent}
                                onChange={(e) => updateRow('memberships', offer.key, { courtBookingDiscountPercent: e.target.value })} />
                            <IconButton aria-label={translate('org_provisioning.remove')} icon={<MdDelete />} variant="ghost"
                                onClick={() => update({ memberships: form.memberships.filter((m) => m.key !== offer.key) })} />
                        </Flex>
                    ))}
                    <Box>
                        <Button size="sm" variant="ghost" leftIcon={<MdAdd />} onClick={() => update({ memberships: [...form.memberships, newMembershipRow()] })}>
                            {translate('org_provisioning.add_membership')}
                        </Button>
                    </Box>
                    <Text {...typography.caption} color={mutedColor}>{translate('org_provisioning.memberships_help')}</Text>
                </FormSection>
            </Stack>
        </PageCard>
    );
}
